import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { FlowStepIcons } from "../components/FlowStepIcons";
import { TemplateFlowPreview } from "../components/TemplateFlowPreview";
import { exploreApi } from "../services/rag-service";
import { useProjects } from "../contexts/ProjectContext";

// Use Template dialog: same 2-field shape as Flows.jsx's CreateFlow dialog
// (display name + project picker), but calls exploreApi.useTemplate instead
// of a blank-flow create.
function UseTemplateDialog({ open, onClose, template, onCreated }) {
  const { t } = useTranslation();
  const { availableProjects } = useProjects();
  const isViewer = !availableProjects.some(
    (project) => project.accessLevel !== "viewer",
  );
  // AP's own first-party "use template" flow pre-fills from the template
  // FLOW's own displayName (not the template's overall title) - falls back
  // to the template's name if that's empty/generic.
  const defaultName = template?.flows?.[0]?.displayName || template?.name || "";
  const [displayName, setDisplayName] = useState(defaultName);
  const [projectId, setProjectId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDisplayName(defaultName);
      setProjectId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const flow = await exploreApi.useTemplate(template.id, {
        display_name: displayName,
        project_id: projectId,
      });
      onClose();
      onCreated(flow);
    } catch (error) {
      console.error("Error creating flow from template:", error);
      toast.error(
        error.message || t("flows.failedToUseTemplate"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("flows.useTemplateDialogTitle")}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <TextField
            label={t("flows.flowName")}
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </FormControl>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="use-template-project-select-label">
            {t("flows.project")}
          </InputLabel>
          <Select
            labelId="use-template-project-select-label"
            label="Project"
            fullWidth
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            {availableProjects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{t("flows.projectHelper")}</FormHelperText>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="secondary" variant="text">
          {t("common.cancel")}
        </Button>
        <Tooltip title={isViewer ? t("flows.viewerCannotCreate") : ""}>
          <span>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isViewer || !displayName || !projectId || submitting}
            >
              {t("flows.useTemplate")}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

const TemplateDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    exploreApi
      .getTemplate(templateId)
      .then((data) => {
        if (!cancelled) {
          setTemplate(data);
        }
      })
      .catch((err) => {
        console.error("Error fetching template detail:", err);
        if (!cancelled) {
          setError(err.message || t("flows.failedToLoadTemplate"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, t]);

  const handleCreated = (flow) => {
    toast.success(t("flows.templateUsedSuccess"));
    navigate(`/flows/${flow.projectId}/${flow.id}`);
  };

  return (
    <DashboardLayout
      title={template?.name || t("flows.exploreTab")}
      fullHeight={true}
      noPadding={true}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: { xs: 1, sm: 2 },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/flows", { state: { activeTab: 2 } })}
          sx={{ mb: 3, alignSelf: "flex-start" }}
        >
          {t("common.back")}
        </Button>

        {loading && (
          <Box>
            <Skeleton variant="text" width="40%" height={48} />
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="80%" height={24} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {template && !loading && (
          <Paper sx={{ overflow: "hidden", flex: 1, minHeight: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "2fr 3fr" },
                height: "100%",
              }}
            >
              <Box sx={{ p: 4, minWidth: 0, height: "100%", overflow: "auto" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    mb: 2,
                    gap: 2,
                  }}
                >
                  <Typography variant="h4" component="h1">
                    {template.name}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setDialogOpen(true)}
                  >
                    {t("flows.useTemplate")}
                  </Button>
                </Box>

                {template.tags && template.tags.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                    {template.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                )}

                {template.description && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 4, whiteSpace: "pre-line" }}
                  >
                    {template.description}
                  </Typography>
                )}

                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  {t("flows.usedPieces")}
                </Typography>
                <FlowStepIcons
                  trigger={template.flows?.[0]?.trigger}
                  maxVisible={10}
                  iconSize={32}
                />
              </Box>

              <Box
                sx={{
                  height: { xs: 480, lg: "100%" },
                  borderLeft: { lg: 1 },
                  borderTop: { xs: 1, lg: 0 },
                  borderColor: "divider",
                }}
              >
                {template.flows?.[0]?.trigger ? (
                  <TemplateFlowPreview trigger={template.flows[0].trigger} />
                ) : null}
              </Box>
            </Box>
          </Paper>
        )}

        <UseTemplateDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          template={template}
          onCreated={handleCreated}
        />
      </Box>
    </DashboardLayout>
  );
};

export default TemplateDetail;
